import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import Sidebar, { SidebarNav, SidebarUserPanel } from '@/components/layout/Sidebar';
import ImpersonationBanner, { getImpersonatingOrg } from '@/components/layout/ImpersonationBanner';
import { MobileHeader, DesktopTopBars, DesktopNotificationBar } from '@/components/layout/TopBars';
import { buildNavGroups, filterNavigation } from '@/components/layout/navConfig';
import { usePendingApprovals } from '@/hooks/usePendingApprovals';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const pendingApprovals = usePendingApprovals(user);
  const { can } = usePermissions();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { items, groupLabels } = filterNavigation(buildNavGroups(t), user, can);
  const impersonatingOrg = getImpersonatingOrg();

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-red-50">
      <ImpersonationBanner org={impersonatingOrg} />

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <SidebarNav items={items} groupLabels={groupLabels} pathname={location.pathname}
          unreadCount={unreadCount} pendingApprovals={pendingApprovals} onNavigate={() => setSidebarOpen(false)} />
        <SidebarUserPanel user={user} roleLabel={t(user?.role)} language={i18n.language}
          onToggleLanguage={toggleLanguage} onProfile={() => navigate('/profile')} onLogout={handleLogout} />
      </Sidebar>

      <div className={`lg:pl-72 relative ${impersonatingOrg ? 'pt-10' : ''}`}>
        <DesktopTopBars />
        <MobileHeader unreadCount={unreadCount} onOpenSidebar={() => setSidebarOpen(true)} onOpenMessages={() => navigate('/messages')} />
        <DesktopNotificationBar />
        <main className="p-6 lg:p-8 relative z-10">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
